import { useCallback, useEffect, useState, Fragment } from 'react';
import { ChevronDown, ChevronRight, Receipt, CalendarCheck, FileText, Undo2 } from 'lucide-react';
import { api } from '../../lib/api';
import { formatMoeda, formatDataSP, formatQtd } from '@fiodecobre/shared';
import { ModalFecharMes } from '../../components/orcamento/ModalFecharMes';
import { ModalExtratoConsignacao } from '../../components/orcamento/ModalExtratoConsignacao';
import { ModalRegistrarDevolucaoVendaCm } from '../../components/orcamento/ModalRegistrarDevolucaoVendaCm';
import { useAuth } from '../../lib/auth';

interface ItemResumo {
  codigo: string | null;
  descricao: string | null;
  quantidade: string;
  preco_unitario: string;
  saldo_valor: string;
}

interface PedidoResumo {
  pedido_id: number;
  numero: string;
  total_saldo_valor: string;
  // Quando o material SAIU (DA-416). Nao e o `criado_em` do orcamento — ver consignacao.routes.ts.
  liberado_em: string;
  // Quem retirou ("Retirado por" do pedido) — vai no extrato do cliente (DA-417).
  comprador: string | null;
  itens: ItemResumo[];
}

interface ClienteConsignacao {
  cliente_id: number;
  cliente_nome: string;
  num_docs: number;
  total_saldo_valor: string;
  pedidos: PedidoResumo[];
}

export function ConsignacaoClientes() {
  // Fechar o mes a vista cria titulo ja quitado — o backend passou a exigir admin (2026-08-12),
  // entao o botao some pros demais em vez de virar 403 no meio do fluxo fiscal.
  const { usuario } = useAuth();
  const podeFecharMes = usuario?.perfil === 'admin';
  const [clientes, setClientes] = useState<ClienteConsignacao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [expandidos, setExpandidos] = useState<Set<number>>(new Set());
  const [pedidosAbertos, setPedidosAbertos] = useState<Set<number>>(new Set());
  const [fechando, setFechando] = useState<ClienteConsignacao | null>(null);
  const [extrato, setExtrato] = useState<ClienteConsignacao | null>(null);
  // Devolucao (DA-416): a ferramenta passou a morar na tela onde o saldo APARECE. Antes so existia
  // dentro do orcamento (banner laranja), duas telas adiante — o Ruan, olhando os saldos aqui,
  // perguntou "como o cliente devolve?", que e o sintoma exato de ferramenta fora de lugar.
  const [devolvendo, setDevolvendo] = useState<PedidoResumo | null>(null);

  const carregar = useCallback(() => {
    setCarregando(true);
    api.get<ClienteConsignacao[]>('/api/consignacao')
      .then(r => setClientes(r))
      .catch(() => setErro('Nao foi possivel carregar as consignacoes.'))
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  function toggleExpandir(clienteId: number) {
    setExpandidos(prev => {
      const next = new Set(prev);
      if (next.has(clienteId)) next.delete(clienteId);
      else next.add(clienteId);
      return next;
    });
  }

  function togglePedido(pedidoId: number) {
    setPedidosAbertos(prev => {
      const next = new Set(prev);
      if (next.has(pedidoId)) next.delete(pedidoId);
      else next.add(pedidoId);
      return next;
    });
  }

  const totalGeral = clientes.reduce((acc, c) => acc + Number(c.total_saldo_valor), 0);

  return (
    <div style={{ padding: '0 4px 40px' }}>
      <div className="pagina-header">
        <h2 className="pagina-titulo">
          <Receipt size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} />
          Consignacao por cliente
          {!carregando && !erro && clientes.length > 0 && (
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginLeft: 10 }}>
              {clientes.length} {clientes.length === 1 ? 'cliente' : 'clientes'} · {formatMoeda(totalGeral)}
            </span>
          )}
        </h2>
      </div>

      {carregando && <p style={{ color: 'var(--text-muted)' }}>Carregando...</p>}
      {erro && <p style={{ color: 'var(--danger)' }}>{erro}</p>}

      {!carregando && !erro && clientes.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>
          <Receipt size={40} strokeWidth={1.5} style={{ margin: '0 auto 12px', display: 'block' }} />
          <p>Nenhuma Venda CM em aberto no momento.</p>
        </div>
      )}

      {!carregando && !erro && clientes.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border)', textAlign: 'left' }}>
              <th style={{ padding: '8px 12px 8px 0', width: 32 }} />
              <th style={{ padding: '8px 12px' }}>Cliente</th>
              <th style={{ padding: '8px 12px', textAlign: 'center', width: 100 }}>Pedidos</th>
              <th style={{ padding: '8px 12px', textAlign: 'right', width: 160 }}>Saldo total</th>
            </tr>
          </thead>
          <tbody>
            {clientes.map(c => {
              const aberto = expandidos.has(c.cliente_id);
              // `Fragment` com key EXPLICITA: antes o `.map` devolvia `<>` (sem key) e a key ficava
              // no <tr> de dentro — o React avisava e, ao expandir/recolher, podia reaproveitar a
              // linha errada. A key pertence ao elemento de TOPO devolvido pelo map.
              return (
                <Fragment key={c.cliente_id}>
                  <tr
                    onClick={() => toggleExpandir(c.cliente_id)}
                    style={{
                      cursor: 'pointer',
                      borderBottom: aberto ? 'none' : '1px solid var(--border)',
                      background: aberto ? 'var(--panel-bg)' : undefined,
                    }}
                  >
                    <td style={{ padding: '10px 0 10px 4px', color: 'var(--text-muted)' }}>
                      {aberto ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </td>
                    <td style={{ padding: '10px 12px', fontWeight: 600 }}>
                      {c.cliente_nome}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center', color: 'var(--text-muted)' }}>
                      {c.num_docs}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700 }}>
                      {formatMoeda(Number(c.total_saldo_valor))}
                    </td>
                  </tr>

                  {/* Sub-linhas na MESMA tabela, nao numa tabela aninhada: aninhada, o navegador
                      distribui as colunas dela por conta propria e, em largura cheia, o Saldo do
                      pedido para longe do Saldo total do cliente — justo a coluna que o olho soma.
                      Aqui cada pedido reusa as 4 colunas da linha mestre, entao o dinheiro fica
                      empilhado numa coluna so, do pedido ao total geral. */}
                  {aberto && c.pedidos.map(p => {
                    const pAberto = pedidosAbertos.has(p.pedido_id);
                    return (
                      <Fragment key={p.pedido_id}>
                        {/* Linha de grade entre os pedidos: com 8 linhas de valores parecidos e
                            todas com a mesma data, o olho perdia a horizontal e lia o saldo de uma
                            linha no numero de outra. Some quando o pedido esta expandido — ali quem
                            separa e o bloco de itens, e a regua viraria ruido no meio do grupo.

                            ⚠️ `--page-bg`, NAO `--border`: medido no tema Azul Royal, `--border` e
                            `#d8e3f7` e o `--panel-bg` deste bloco e `#cfdef6` — a borda e mais CLARA
                            que o fundo dela, entao some. `--border` foi calibrado pra fundo branco.
                            O `--page-bg` e a cor que contrasta com o painel por definicao (e o que
                            aparece em volta dele), e serve nos dois sentidos: claro no tema escuro,
                            escuro no tema claro. Regua dentro de bloco pintado usa esta. */}
                        <tr
                          onClick={() => togglePedido(p.pedido_id)}
                          style={{
                            cursor: 'pointer',
                            background: 'var(--panel-bg)',
                            fontSize: 13,
                            borderBottom: pAberto ? 'none' : '1px solid var(--page-bg)',
                          }}
                        >
                          <td />
                          <td style={{ padding: '5px 12px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, paddingLeft: 18 }}>
                              <span style={{ color: 'var(--text-muted)', display: 'inline-flex' }}>
                                {pAberto ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              </span>
                              <span style={{ fontWeight: 600 }}>{p.numero}</span>
                              <span style={{ color: 'var(--text-muted)' }}>
                                liberado em {formatDataSP(p.liberado_em)}
                              </span>
                            </span>
                          </td>
                          {/* Acao PRIMARIA dentro de grade e botao solido, nao link nem menu de 1
                              item. `stopPropagation` porque a linha inteira e o toggle dos itens —
                              sem isso, devolver tambem sanfonaria a linha. */}
                          <td style={{ padding: '5px 12px', textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setDevolvendo(p); }}
                              className="btn-primario"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, padding: '5px 12px' }}
                              title={`Registrar devolucao do ${p.numero}`}
                            >
                              <Undo2 size={14} /> Devolver
                            </button>
                          </td>
                          <td style={{ padding: '5px 12px', textAlign: 'right' }}>
                            {formatMoeda(Number(p.total_saldo_valor))}
                          </td>
                        </tr>

                        {pAberto && (
                          <tr style={{ background: 'var(--panel-bg)', borderBottom: '1px solid var(--page-bg)' }}>
                            <td />
                            <td colSpan={3} style={{ padding: '2px 12px 10px 42px' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                                <thead>
                                  <tr style={{ color: 'var(--text-muted)' }}>
                                    <th style={{ padding: '3px 8px 3px 0', textAlign: 'left', fontWeight: 400, width: 56 }}>Cod.</th>
                                    <th style={{ padding: '3px 8px', textAlign: 'left', fontWeight: 400 }}>Produto</th>
                                    <th style={{ padding: '3px 8px', textAlign: 'right', fontWeight: 400, width: 70 }}>Qtd</th>
                                    <th style={{ padding: '3px 8px', textAlign: 'right', fontWeight: 400, width: 90 }}>Unit.</th>
                                    <th style={{ padding: '3px 0 3px 8px', textAlign: 'right', fontWeight: 400, width: 90 }}>Saldo</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(p.itens ?? []).map((it, i) => (
                                    <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                                      <td style={{ padding: '3px 8px 3px 0', fontFamily: 'monospace' }}>{it.codigo ?? '—'}</td>
                                      <td style={{ padding: '3px 8px' }}>{it.descricao ?? '—'}</td>
                                      <td style={{ padding: '3px 8px', textAlign: 'right' }}>{formatQtd(it.quantidade)}</td>
                                      <td style={{ padding: '3px 8px', textAlign: 'right' }}>{formatMoeda(Number(it.preco_unitario))}</td>
                                      <td style={{ padding: '3px 0 3px 8px', textAlign: 'right', fontWeight: 600 }}>{formatMoeda(Number(it.saldo_valor))}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}

                  {/* Extrato e Fechar o mes agem no CLIENTE INTEIRO — por isso ficam no rodape do
                      bloco, e nao na linha do pedido (onde mora o Devolver, que e por pedido). */}
                  {aberto && (
                    <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--panel-bg)' }}>
                      <td />
                      <td colSpan={3} style={{ padding: '4px 12px 12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                          <button
                            type="button"
                            onClick={() => setExtrato(c)}
                            className="btn-secundario"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '7px 14px' }}
                          >
                            <FileText size={15} /> Extrato
                          </button>
                          {podeFecharMes && (
                            <button
                              type="button"
                              onClick={() => setFechando(c)}
                              className="btn-primario"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '7px 14px' }}
                            >
                              <CalendarCheck size={15} /> Fechar o mes
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid var(--border)' }}>
              <td />
              <td style={{ padding: '10px 12px', fontWeight: 700 }}>Total geral</td>
              <td />
              <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700 }}>
                {formatMoeda(totalGeral)}
              </td>
            </tr>
          </tfoot>
        </table>
      )}

      {extrato && (
        <ModalExtratoConsignacao
          clienteId={extrato.cliente_id}
          clienteNome={extrato.cliente_nome}
          pedidos={extrato.pedidos}
          total={Number(extrato.total_saldo_valor)}
          onClose={() => setExtrato(null)}
        />
      )}

      {devolvendo && (
        <ModalRegistrarDevolucaoVendaCm
          orcamentoId={devolvendo.pedido_id}
          onClose={() => setDevolvendo(null)}
          onSucesso={() => { setDevolvendo(null); carregar(); }}
        />
      )}

      {fechando && (
        <ModalFecharMes
          clienteId={fechando.cliente_id}
          clienteNome={fechando.cliente_nome}
          totalSaldo={Number(fechando.total_saldo_valor)}
          onClose={() => setFechando(null)}
          onSucesso={() => { setFechando(null); carregar(); }}
        />
      )}
    </div>
  );
}
